/**
 * Wire types for the BFF snapshot response + a runtime type-guard
 * that validates incoming payloads at the trust boundary.
 *
 * # Types come from generated OpenAPI codegen
 *
 * The BFF's ``/api/query/snapshot`` route now declares a Pydantic
 * :class:`SnapshotResponse` response_model (see
 * ``packages/meridian_bff/src/meridian_bff/_query_api.py`` and
 * ``_query_routes.py``). The codegen reads the committed
 * ``packages/meridian_bff/api/openapi.yaml`` and surfaces typed
 * shapes at ``components["schemas"]["StatusItem"]`` /
 * ``components["schemas"]["SnapshotResponse"]``. This module
 * re-exports those under the UI's local names + adds the runtime
 * guards used at the trust boundary.
 *
 * # Why we still need a runtime guard
 *
 * TypeScript types are erased at runtime. A server bug, schema
 * drift, MSW fixture typo, or compromised upstream could deliver a
 * payload that doesn't match the typed contract. CLAUDE.md §2.2
 * mandates ``zod`` at trust boundaries; ``zod`` is not yet a UI
 * dependency (the tech-stack add + LICENSING update is deferred to
 * M3 where SSE message validation makes it a forcing function).
 * The hand-rolled :func:`isSnapshotResponse` is the M2 stand-in for
 * that discipline.
 *
 * Cross-references:
 *   - REQ-UI-003 (snapshot on map open).
 *   - REQ-UI-035 (BFF-only).
 *   - CLAUDE.md §2.2 (zod at trust boundaries — deferred to M3).
 *   - CLAUDE.md §5.10 (typed openapi.yaml is single source of truth).
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py §StatusItem
 *     / §SnapshotResponse.
 */

import type { HealthState } from "@/map/marker-styling";
import type { components } from "@/lib/api-types.gen";

/**
 * One Producer position record in a snapshot — generated from the
 * BFF's Pydantic :class:`StatusItem` via ``openapi-typescript``.
 *
 * The local alias preserves the M2-era ``SnapshotItem`` name across
 * M3+ commits without churn; if the BFF schema name ever drifts
 * from ``StatusItem`` the alias update is contained here.
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py §StatusItem.
 *   - schemas/proto/meridian/v1/data/status.proto.
 */
export type SnapshotItem = components["schemas"]["StatusItem"];

/**
 * The BFF's snapshot response envelope — generated from the BFF's
 * Pydantic :class:`SnapshotResponse`.
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py §SnapshotResponse.
 *   - packages/meridian_bff/src/meridian_bff/_query_routes.py §snapshot_route.
 */
export type SnapshotResponse = components["schemas"]["SnapshotResponse"];

/**
 * Viewport bounds used by :func:`useSnapshot` to compose the query
 * string the BFF expects.
 *
 * UI-side type (the BFF accepts these as four query-string floats
 * rather than a single body object), so this is local-only and not
 * a re-export.
 *
 * Cross-references:
 *   - REQ-UI-003 (snapshot request on map open).
 *   - packages/meridian_bff/src/meridian_bff/_query_records.py §Viewport.
 */
export interface SnapshotViewport {
  min_longitude: number;
  min_latitude: number;
  max_longitude: number;
  max_latitude: number;
}

/**
 * Type guard: validate that an arbitrary value matches the
 * :type:`SnapshotResponse` shape closely enough that downstream
 * consumers can rely on the field set.
 *
 * Hand-rolled at the trust boundary in lieu of ``zod`` (see module
 * docstring). The guard is intentionally permissive on numeric
 * fields (accepts ``number | null`` for the optional ones) and
 * strict on string fields the marker layer reads (``producer_id``,
 * ``latitude_deg``, ``longitude_deg``, ``health_state``).
 *
 * Returns ``false`` on first deviation. Callers should treat
 * ``false`` as a 5xx-shaped failure and surface a fail-safe state
 * (operator-visibility per CLAUDE.md §8.1).
 */
export function isSnapshotResponse(value: unknown): value is SnapshotResponse {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.items)) {
    return false;
  }
  if (typeof obj.duration_ms !== "number") {
    return false;
  }
  return obj.items.every(isSnapshotItem);
}

/**
 * Per-item type guard. Exported for unit testability; consumers
 * outside the tests should generally call :func:`isSnapshotResponse`
 * on the envelope.
 */
export function isSnapshotItem(value: unknown): value is SnapshotItem {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.producer_id !== "string") return false;
  if (typeof obj.time !== "string") return false;
  if (typeof obj.latitude_deg !== "number") return false;
  if (typeof obj.longitude_deg !== "number") return false;
  if (typeof obj.elevation_m !== "number") return false;
  if (typeof obj.schema_version !== "number") return false;
  if (typeof obj.health_state !== "number") return false;
  if (typeof obj.mode !== "number") return false;
  if (typeof obj.source_aggregator_id !== "string") return false;
  if (typeof obj.ingress_timestamp !== "string") return false;
  if (typeof obj.message_hash !== "string") return false;
  // Nullable fields — allow null OR the expected primitive.
  // ``firmware_version`` / ``temperature_c`` / ``signal_strength_dbm`` were
  // dropped from the BFF ``StatusItem`` in the ADR-032 D11 SAPIENT canonical
  // re-base (they had no BSI slot), so the guard no longer requires them —
  // requiring an absent field here rejected every snapshot item and blanked
  // the map (the UI-cascade catch-up for #377).
  if (obj.battery_pct !== null && typeof obj.battery_pct !== "number") {
    return false;
  }
  return true;
}

/**
 * Narrow ``SnapshotItem.health_state`` (declared as ``number`` to
 * mirror the wire) into a :type:`HealthState` literal so callers
 * can ``switch`` on it exhaustively.
 *
 * Unknown integer values are surfaced to the caller as-is; the
 * marker-styling layer logs + degrades, this guard does not.
 */
export function asHealthState(value: number): HealthState {
  // The cast is safe at this boundary: callers always pass a
  // value that originated in the wire response, and the
  // marker-styling switch handles the unknown-integer case.
  return value as HealthState;
}

/**
 * Build a ``producer_id`` → ``[longitude_deg, latitude_deg]`` index from a
 * snapshot response, for anchoring unprojectable range/bearing detections at
 * their reporting platform marker (REQ-UI-071;
 * :func:`map/detection-layer-impl.detectionsToFeatureCollection`).
 *
 * Viewport-scoped, like the snapshot itself — so only on-screen platforms can
 * anchor a detection, which is exactly when the anchor would be visible. A
 * ``null`` snapshot (load / refetch in flight) yields an empty index. An item
 * lacking a numeric position is skipped defensively: a platform that reported
 * no ``node_location`` of its own has no marker to anchor to, so its
 * detections correctly stay list-only.
 *
 * Cross-references:
 *   - REQ-UI-071 (range/bearing anchored at the reporting platform).
 *   - map/detection-layer-impl.ts (the anchor consumer).
 *   - map/Map.tsx (derives this from the live snapshot query).
 */
export function buildPlatformPositionIndex(
  snapshot: SnapshotResponse | null,
): Map<string, [number, number]> {
  const index = new Map<string, [number, number]>();
  if (snapshot === null) {
    return index;
  }
  for (const item of snapshot.items) {
    if (typeof item.latitude_deg === "number" && typeof item.longitude_deg === "number") {
      index.set(item.producer_id, [item.longitude_deg, item.latitude_deg]);
    }
  }
  return index;
}

/**
 * Type guard: a snapshot / trail row that carries a usable map position.
 *
 * ``StatusItem`` position is nullable on the wire — a status reported with no
 * node location (REQ-INGEST-044) — but the map-oriented snapshot / trail BFF
 * queries exclude such rows, so at runtime they always carry a position. This
 * predicate narrows the nullable position for the marker / polyline builders
 * and defensively drops any location-less row: it has no map marker and
 * surfaces only in the Entity Details STATUS panel (operator-visibility per
 * CLAUDE.md §8.1).
 */
export function hasMapPosition<
  T extends { latitude_deg: number | null; longitude_deg: number | null },
>(item: T): item is T & { latitude_deg: number; longitude_deg: number } {
  return item.latitude_deg !== null && item.longitude_deg !== null;
}
